import Phaser from 'phaser'
import { makeButton, makeImageTextButton } from '@/ui/button'
import { AudioManager } from '@/services/AudioManager'
import { useGameStore } from '@/store/gameStore'
import { SHOP_ITEMS, getPoints, getItemCount, type ShopItemDef } from '@/services/shop'

/** 대기실: 보유 아이템을 하단 일렬로 보여주고, 탭하면 설명+사용여부 팝업. 장착 후 게임 시작. */
export class WaitingRoom extends Phaser.Scene {
  private equip: Record<string, boolean> = {}
  private tileRefresh: Record<string, () => void> = {}
  private dialog?: Phaser.GameObjects.Container

  constructor() { super('WaitingRoom') }

  create() {
    this.equip = {}; this.tileRefresh = {}; this.dialog = undefined
    const { width, height } = this.scale
    this.add.image(width / 2, height / 2, 'bg_lobby').setDisplaySize(width, height)
    this.add.rectangle(width / 2, height / 2, width, height, 0x10082a, 0.45)
    AudioManager.init(this)
    AudioManager.startBgm()

    this.add.text(width / 2, height * 0.1, '게임 준비', {
      fontFamily: 'Arial', fontSize: `${Math.round(width * 0.085)}px`, color: '#ffffff', fontStyle: 'bold',
      stroke: '#3a2a6a', strokeThickness: Math.round(width * 0.012),
    }).setOrigin(0.5)

    const nick = (useGameStore.getState().nickname || '나').trim() || '나'
    this.add.text(width / 2, height * 0.18, `${nick}  ·  💰 ${getPoints().toLocaleString()} P`, {
      fontFamily: 'Arial', fontSize: `${Math.round(width * 0.042)}px`, color: '#ffe066', fontStyle: 'bold',
      stroke: '#000000', strokeThickness: 3,
    }).setOrigin(0.5)

    // 게임 시작(메인) + 뒤로/상점 (이미지 버튼)
    makeImageTextButton(this, width / 2, height * 0.44, 'btn_enter', '게임 시작',
      () => this.scene.start('Game', { mode: 'challenge', equip: this.equip }),
      { width: width * 0.48, fontSize: width * 0.042 })
    makeImageTextButton(this, width * 0.3, height * 0.55, 'btn_back', '뒤로', () => this.scene.start('Lobby'),
      { width: width * 0.38, fontSize: width * 0.038 })
    makeImageTextButton(this, width * 0.7, height * 0.55, 'btn_shop', '상점', () => this.scene.start('Shop'),
      { width: width * 0.38, fontSize: width * 0.038 })

    // 보유 아이템(하단 일렬)
    this.add.text(width / 2, height * 0.7, '보유 아이템 · 눌러서 확인 (포인트2배만 장착)', {
      fontFamily: 'Arial', fontSize: `${Math.round(width * 0.034)}px`, color: '#ffffff',
    }).setOrigin(0.5).setAlpha(0.85)

    const owned = SHOP_ITEMS.filter(it => getItemCount(it.key) > 0)
    if (owned.length === 0) {
      this.add.text(width / 2, height * 0.83, '보유한 아이템이 없어요\n🛒 상점에서 구매하세요', {
        fontFamily: 'Arial', fontSize: `${Math.round(width * 0.038)}px`, color: '#cdbfff', align: 'center',
      }).setOrigin(0.5).setAlpha(0.85)
    } else {
      const n = owned.length
      // 개수가 많을수록 타일을 작게 → 아이템 간 간격 여유(간격 = 타일의 약 32%)
      const s = Math.min(width * 0.15, (width * 0.9) / (1.32 * n - 0.32))
      const step = s * 1.32
      const startX = width / 2 - (step * (n - 1)) / 2
      owned.forEach((it, i) => this.makeItemTile(startX + i * step, height * 0.83, it, s))
    }
  }

  private makeItemTile(x: number, y: number, it: ShopItemDef, s: number) {
    const { width } = this.scale
    const g = this.add.graphics()
    const ikey = `item_${it.key}`
    if (this.textures.exists(ikey)) this.add.image(x, y, ikey).setDisplaySize(s * 0.78, s * 0.78)
    else this.add.text(x, y, it.emoji, { fontSize: `${Math.round(width * 0.07)}px` }).setOrigin(0.5)
    this.add.text(x + s * 0.44, y - s * 0.44, `${getItemCount(it.key)}`, {
      fontFamily: 'Arial', fontSize: `${Math.round(width * 0.026)}px`, color: '#ffffff', fontStyle: 'bold',
      backgroundColor: '#e0556b', padding: { x: 3, y: 1 },
    }).setOrigin(0.5).setDepth(2)
    const check = this.add.text(x - s * 0.42, y - s * 0.42, '✓', {
      fontFamily: 'Arial', fontSize: `${Math.round(width * 0.034)}px`, color: '#7ef0c2', fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(2).setVisible(false)
    const draw = () => {
      const on = !!this.equip[it.key]
      g.clear()
      g.fillStyle(on ? 0x2e7d5b : 0x140d2e, 0.85); g.fillRoundedRect(x - s / 2, y - s / 2, s, s, s * 0.22)
      g.lineStyle(Math.max(2, width * 0.005), on ? 0x7ef0c2 : 0xffffff, on ? 0.95 : 0.3)
      g.strokeRoundedRect(x - s / 2, y - s / 2, s, s, s * 0.22)
      check.setVisible(on)
    }
    draw()
    this.tileRefresh[it.key] = draw
    this.add.zone(x, y, s, s).setInteractive({ useHandCursor: true }).on('pointerup', () => this.openItemDialog(it))
  }

  /** 아이템 설명 + 사용/취소 팝업 */
  private openItemDialog(it: ShopItemDef) {
    if (this.dialog) return
    const { width, height } = this.scale
    const c = this.add.container(0, 0).setDepth(100)
    const dim = this.add.rectangle(width / 2, height / 2, width, height, 0x000000, 0.55).setInteractive()
    dim.on('pointerup', () => this.closeDialog())
    const pw = width * 0.8, ph = height * 0.36
    const g = this.add.graphics()
    g.fillStyle(0x1a1140, 0.98); g.fillRoundedRect(width / 2 - pw / 2, height / 2 - ph / 2, pw, ph, ph * 0.12)
    g.lineStyle(2, 0xffffff, 0.3); g.strokeRoundedRect(width / 2 - pw / 2, height / 2 - ph / 2, pw, ph, ph * 0.12)
    const ikey = `item_${it.key}`
    const emo: Phaser.GameObjects.GameObject = this.textures.exists(ikey)
      ? this.add.image(width / 2, height / 2 - ph * 0.28, ikey).setDisplaySize(width * 0.16, width * 0.16)
      : this.add.text(width / 2, height / 2 - ph * 0.3, it.emoji, { fontSize: `${Math.round(width * 0.1)}px` }).setOrigin(0.5)
    const name = this.add.text(width / 2, height / 2 - ph * 0.06, it.name, {
      fontFamily: 'Arial', fontSize: `${Math.round(width * 0.05)}px`, color: '#ffffff', fontStyle: 'bold',
    }).setOrigin(0.5)
    const note = it.usage === 'equip' ? '\n(게임 시작 전 장착)'
      : it.usage === 'auto' ? '\n(게임오버 시 사용 여부 선택)'
      : '\n(게임 중 버튼으로 사용)'
    const desc = this.add.text(width / 2, height / 2 + ph * 0.1, it.desc + note, {
      fontFamily: 'Arial', fontSize: `${Math.round(width * 0.035)}px`, color: '#cdbfff', align: 'center',
      wordWrap: { width: pw * 0.86 },
    }).setOrigin(0.5)
    c.add([dim, g, emo, name, desc])
    if (it.usage === 'equip') {
      // 장착형(포인트2배): 사용/취소 토글
      const equipped = !!this.equip[it.key]
      makeButton(this, width / 2 - pw * 0.22, height / 2 + ph * 0.34, equipped ? '사용 안함' : '사용하기',
        () => { this.equip[it.key] = !equipped; this.tileRefresh[it.key]?.(); this.closeDialog() },
        { parent: c, width: pw * 0.4, height: height * 0.07, fontSize: width * 0.04,
          color: equipped ? 0xff8a8a : 0x5ec8a0, pressColor: equipped ? 0xe87070 : 0x49ad88 })
      makeButton(this, width / 2 + pw * 0.22, height / 2 + ph * 0.34, '취소', () => this.closeDialog(),
        { parent: c, width: pw * 0.4, height: height * 0.07, fontSize: width * 0.04, color: 0xa98bff, pressColor: 0x8f6fe8 })
    } else {
      // 인게임/자동형: 설정 불필요 → 확인만
      makeButton(this, width / 2, height / 2 + ph * 0.34, '확인', () => this.closeDialog(),
        { parent: c, width: pw * 0.5, height: height * 0.07, fontSize: width * 0.04, color: 0xa98bff, pressColor: 0x8f6fe8 })
    }
    this.dialog = c
  }

  private closeDialog() {
    this.dialog?.destroy()
    this.dialog = undefined
  }
}
